import
  React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  useHistory,
  useParams
} from 'react-router-dom';

import {
  JourneyProvider,
  TileCard,
  VContainer,
  VContainerVH
} from '@vodacom/web-common';

import {
  Environment
} from '@vodacom/web-env';

import
  styled
from 'styled-components';

import {
  CacheKeys,
  ErrorKeys,
  FeatureToggles,
  OriginContexts
} from '../constants';

import {
  BackButton,
  Hero,
  PrimaryButton
} from 'components';

import {
  HandleRender,
  withScreenViewTagging
} from 'hocs';

import {
  DrupalProvider
} from 'providers';

import {
  handleUtagLinkEvent,
  isEqual,
  isFeatureEnabled,
  openDeeplink,
  toKebabCase,
  useRedirectHandler
} from 'helpers';

import {
  AppContext
} from 'context';

const TileListStyled = styled.div`
  flex: 1;
  margin: 0 0 16px;

  h1, h2 {
    padding: 0;
    margin: 0;
  }

  h1 {
    font-size: 26px;
    text-transform: capitalize;
    margin-bottom: 16px;
  }

  h2 {
    font-size: 18px;
    margin-top: 64px;
    margin-bottom: 8px;
  }
`;

const PillarListUI = () => {

  const BASE_URL = Environment.current.drupalBaseUrl;
  const history = useHistory();
  const { config } = useContext(AppContext);
  const { pillar } = useParams();
  const mountedRef = useRef(true);
  const [ pillarTiles, setTiles ] = useState(null);
  const [ pillarMeta, setMeta ] = useState(null);
  const [ state, setState ] = useState({
    loading: true,
    hasError: false,
    error: null,
    faqPage: null
  });

  const context = JourneyProvider.get(CacheKeys.ConfigContext, 'value', '');
  const redirectHandler = useRedirectHandler();

  const getFaqPages = async () => {

    const faqPages = await DrupalProvider.getCachedItem('faq_pages', null, DrupalProvider.getFaqPages);

    if (!faqPages || !faqPages.ok) {

      return;
    }

    setState(prevState => ({
      ...prevState,
      faqPage: faqPages?.result?.find(page => isEqual(page.title, 'Covid FAQs'))
    }));
  };

  PillarListUI.pageName = `cbu: connectu: free services: ${pillar}: content page`;

  const buildAdditionalButtons = (
    buttons,
    tile,
    isAdvance
  ) => {

    if (!Array.isArray(buttons)) {
      return;
    }

    const buttonList = [];

    for (const button of buttons) {

      const buttonID = `additional-button-${button?.value?.field_click_out_url?.uri}`;

      const onButtonClick = () => {

        if (button.value?.field_vodapay_app_id && isEqual(context, OriginContexts.VodaPay)) {

          openDeeplink(button.value.field_vodapay_app_id);

          return;
        }

        if (isAdvance && isEqual(button?.value?.field_button_text?.value, 'Buy now')) {

          if (isFeatureEnabled(config, FeatureToggles.EnableAirtimeAdvance)) {

            history.push({
              pathname: '/advance',
              state: {
                item: tile,
                hideButton: true
              }
            });

            return;
          }
        }

        redirectHandler(
          history.location,
          `/redirect?targetUrl=${encodeURIComponent(button?.value?.field_click_out_url?.uri)}&` +
          `isZeroRated=${button?.value?.field_is_url_zero_rated}&` +
          `isSubscription=${tile.isSubscription}&` +
          `id=${encodeURIComponent(tile.id)}`,
          button?.value?.field_is_url_zero_rated
        );

        return;
      };

      buttonList.push(

        <PrimaryButton
          id={buttonID}
          automationId={buttonID}
          key={buttonID}
          width={'92%'}
          maxWidth={'92%'}
          text={button?.value?.field_button_text?.value}
          onClick={onButtonClick}/>
      );
    }

    return buttonList;
  };

  const buildPillarTiles = () => {
    const tiles = [];

    for (const [ index, tile ] of pillarTiles.entries()) {

      if (!tile || (isEqual(context, OriginContexts.VodaPay)  && tile.hideOnVodapay)) {
        continue;
      }

      if (index === 0 && isEqual(pillar, 'health') && state.faqPage) {
        tiles.push(

          <TileCard
            key={'tile-0.0'}
            title={state.faqPage?.header}
            text={''}
            image={`${Environment.current.drupalBaseUrl}${state.faqPage?.headerImage}`}
            imageHeight={'240px'}
            imageObjectFit={'cover'}
            buttonText={'Read More'}
            buttonMinWidth={'92%'}
            subtitle={''}
            buttonActiveOpacity={'0.25'}
            onClick={() => {

              history.push({
                pathname: '/faq',
                search: `?faqPage=${encodeURIComponent(state.faqPage?.title)}`
              });
            }} />
        );
      }

      const tileId = `${toKebabCase(tile.title)}-button`;
      const isAdvance = tile.title.toLowerCase().includes('airtime') && tile.title.toLowerCase().includes('advance');

      tiles.push(

        <TileCard
          key={`tile-${index}`}
          image={
            tile.image
              ? `${BASE_URL}${tile.image}`
              : ''
          }
          subtitle={tile.category}
          title={tile.title}
          text={tile.short_description || ''}
          isTextHTML
          buttonMinWidth={'92%'}
          buttonText={tile.pillar_content_button_text}
          buttonId={tileId}
          buttonAutomationId={tileId}
          buttonActiveOpacity={'0.25'}
          onClick={() => {

            handleUtagLinkEvent({
              event_action: 'click',
              event_category: 'free services content',
              event_label: tile.title
            });

            if (tile.vodaypayAppId && isEqual(context, OriginContexts.VodaPay)) {

              openDeeplink(tile.vodaypayAppId);

              return;
            }

            if (!tile.has_connectu_page) {

              redirectHandler(
                history.location,
                `/redirect?targetUrl=${encodeURIComponent(tile.click_out_url.uri)}&isZeroRated=${tile.is_zero_rated}`,
                tile.is_zero_rated
              );

              return;
            }

            if (isAdvance) {

              if (tile.pillar_content_button_text !== 'Buy now') {

                history.push({
                  pathname: `/pillars/${pillar}/${tile.id}`,
                  state: {
                    item: tile,
                    hideButton: true
                  }
                });

                return;
              }

              if (!isFeatureEnabled(config, FeatureToggles.EnableAirtimeAdvance)) {

                redirectHandler(
                  history.location,
                  `/redirect?targetUrl=${tile.click_out_url.uri}&isZeroRated=${tile.is_zero_rated}`,
                  tile.is_zero_rated
                );

                return;
              }

              history.push({
                pathname: '/advance',
                state: {
                  item: tile,
                  hideButton: true
                }
              });

              return;
            }

            history.push({
              pathname: `/pillars/${pillar}/${tile.id}`,
              state: {
                item: tile
              }
            });
          }}>

          <VContainerVH>
            { buildAdditionalButtons(tile.additionalButtons, tile, isAdvance) }
          </VContainerVH>

        </TileCard>
      );
    }

    return tiles;
  };

  const getPillarTiles = useCallback(async () => {

    const response = await DrupalProvider.getCachedItem(
      `${CacheKeys.Pillars}${pillar}`,
      null,
      function () { return DrupalProvider.getPillarTiles(pillar); }
    );

    if (!mountedRef.current) {
      return;
    }

    if (!response?.ok) {

      setState(prevState => ({
          ...prevState,
          hasError: true,
          error: response?.error
      }));

      return;
    }

    setTiles(response.result);

  }, [ pillar ]);

  const getPillarMeta = useCallback(async () => {

    const response = await DrupalProvider.getCachedItem(
      CacheKeys.PillarMeta,
      null,
      DrupalProvider.getPillarMeta
    );

    if (!mountedRef.current) {
      return;
    }

    if (!response.ok) {

      setState(prevState => ({
          ...prevState,
          hasError: true,
          error: response.error
      }));

      return;
    }

    const currentPillarMeta = response.result.find((item) => isEqual(item.category, pillar));

    if (!currentPillarMeta) {

      setState(prevState => ({
          ...prevState,
          hasError: true,
          error: new Error('Page not found')
      }));

      return;
    }

    setMeta(currentPillarMeta);

  }, [ pillar ]);

  useEffect(() => {

    mountedRef.current = true;

    setState(prevState => ({
      ...prevState,
      loading: true
    }));

    const init = async () => {

      if (isEqual(pillar, 'health')) {

        getFaqPages();
      }

      await getPillarMeta();
      await getPillarTiles();

      setState(prevState => ({
        ...prevState,
        loading: false
      }));

    };

    init();

    return () => {
      mountedRef.current = false;
    };
  }, [ getPillarMeta, getPillarTiles, pillar ]);

  const getNumberOfServices = () => {
    let count = 0;
    if (pillarTiles?.length > 0) {
      if (isEqual(pillar, 'health') && state.faqPage) {
        count ++;
      }
      count += pillarTiles.length;
    }
    return count;
  };

  return (

    <HandleRender
      isLoading={state.loading}
      isError={state.hasError}
      error={{ type: ErrorKeys.Technical, title: `The ${pillar} page does not exist, please go back to the home page` }}>

      <TileListStyled>
      {
        pillarMeta &&
        <Hero
          background={`${Environment.current.drupalBaseUrl}${pillarMeta.heroImage}`}
          useOverLay={true}
          subtitle={`${getNumberOfServices()} Services`}
          title={pillarMeta.title}
          caption={pillarMeta.description} />
      }

        <BackButton onClick={() => history.push('/home')} />

        <VContainer padding='0 16px'>
          {
            pillarTiles && pillarTiles.length > 0 &&
            buildPillarTiles()
          }
        </VContainer>
      </TileListStyled>
    </HandleRender>
  );
};

PillarListUI.tagViewData = {
  page_section: 'free service: content page'
};

export const PillarList = withScreenViewTagging(PillarListUI);
